import { io } from 'socket.io-client';
import { API_ORIGIN } from './axios';

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false; // renamed to avoid shadowing method name
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.maxReconnectDelay = 30000;
    this.eventListeners = new Map();
    this.reconnectTimeout = null;
  }

  /**
   * Initialize Socket.io connection with JWT authentication
   */
  init() {
    // Clear any existing reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // If no auth token, do not attempt a connection
    const token = this.getAuthToken();
    if (!token) {
      // Ensure any existing socket is fully cleaned up
      if (this.socket) {
        try {
          this.socket.removeAllListeners();
          this.socket.disconnect();
        } catch {}
        this.socket = null;
      }
      this.connected = false;
      this.reconnectAttempts = 0;
      return null;
    }

    // Use the same normalized origin as the Axios client to avoid env mismatches
    const origin = API_ORIGIN;
    
    // Close existing connection if any
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }
    
    // Environment-aware transports: prefer websocket-only in production to avoid polling CORS issues
    const preferWsOnly = import.meta.env.PROD;
    
    // Create socket connection
    this.socket = io(origin, {
      path: '/socket.io',
      // Production: WebSocket-only (avoids XHR polling CORS complexity)
      // Development: Allow polling fallback for local proxy compatibility
      transports: preferWsOnly ? ['websocket'] : ['polling', 'websocket'],
      reconnection: false, // we handle reconnection manually
      timeout: 60000,
      withCredentials: false, // do not send cookies; rely on JWT in auth
      forceNew: true,
      auth: {
        token
      }
    });

    // Set up event listeners
    this.setupEventListeners();
    
    // Attempt connection
    this.socket.connect();
    
    return this.socket;
  }

  /**
   * Get JWT token from localStorage
   */
  getAuthToken() {
    return (
      localStorage.getItem('auth_token') ||
      localStorage.getItem('mgx_token') ||
      sessionStorage.getItem('auth_token') ||
      sessionStorage.getItem('mgx_token') ||
      null
    );
  }

  /**
   * Set up Socket.io event listeners
   */
  setupEventListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('Socket connected successfully');
      this.connected = true;
      this.reconnectAttempts = 0;
      // Clear any reconnect timeout
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
      // Emit connection event to notify listeners
      this.emitToHandlers('socket_connect');
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      this.connected = false;
      
      // Handle different disconnection reasons
      if (reason === 'io server disconnect') {
        // Server actively disconnected, don't reconnect automatically
        console.log('Server initiated disconnection');
      } else if (reason === 'io client disconnect') {
        // Client initiated disconnection
        console.log('Client initiated disconnection');
      } else {
        // Unexpected disconnection, attempt to reconnect
        this.handleReconnection();
      }
      
      // Emit disconnection event to notify listeners
      this.emitToHandlers('socket_disconnect', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      this.connected = false;
      this.handleReconnection();
      // Emit connection error event to notify listeners
      this.emitToHandlers('socket_connect_error', error);
    });

    // Forward all other events to registered handlers
    this.socket.onAny((event, ...args) => {
      this.emitToHandlers(event, ...args);
    });
  }

  /**
   * Handle reconnection logic with exponential backoff
   */
  handleReconnection() {
    // Abort reconnection loop if no auth token is available
    if (!this.getAuthToken()) {
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Maximum reconnection attempts reached');
      this.emitToHandlers('socket_reconnect_failed');
      return;
    }

    this.reconnectAttempts++;
    
    // Calculate delay with exponential backoff (max 30 seconds)
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );
    
    console.log(`Reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);
    this.emitToHandlers('socket_reconnect_attempt', this.reconnectAttempts);
    
    // Schedule reconnection
    this.reconnectTimeout = setTimeout(() => {
      console.log('Attempting to reconnect...');
      this.init();
    }, delay);
  }

  /**
   * Update authentication token
   */
  updateAuthToken(token) {
    if (token) {
      if (this.socket) {
        this.socket.auth = { token };
        if (this.connected) {
          this.socket.disconnect().connect();
        } else {
          // No active connection: (re)initialize
          this.init();
        }
      } else {
        // No socket yet; initialize
        this.init();
      }
    }
  }

  /**
   * Emit an event to the server
   */
  emit(event, data) {
    if (this.socket && this.isConnected()) {
      return this.socket.emit(event, data);
    } else {
      console.warn('Socket not connected. Cannot emit event:', event);
      return false;
    }
  }

  /**
   * Listen for events from the server
   */
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(callback);
  }

  /**
   * Remove event listener
   */
  off(event, callback) {
    if (this.eventListeners.has(event)) {
      const listeners = this.eventListeners.get(event);
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  /**
   * Emit event to registered handlers
   */
  emitToHandlers(event, ...args) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach(callback => {
        try {
          callback(...args);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Disconnect the socket
   */
  disconnect() {
    // Clear any reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.connected = false;
      this.reconnectAttempts = 0;
    }
  }

  /**
   * Check if socket is connected
   */
  isConnected() {
    return this.connected && this.socket && this.socket.connected;
  }

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return {
      connected: this.isConnected(),
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts
    };
  }
}

// Create and export a singleton instance
const socketService = new SocketService();
export default socketService;